'use client'

import { useTranslation } from 'next-i18next'
import { Dispatch, SetStateAction } from 'react'

import { STATSIG_EXPERIMENTS_CONFIG } from '@src/config/statsig-experiments.config'
import { STATSIG_EXPERIMENTS, useStatsigExperiment } from '@src/experiments/statsig-integration'
import { useQuinceRouter as useRouter } from '@src/modules/navigation'
import { Link } from '@src/quince-components/src'
import { BUTTON_SIZE, BUTTON_TYPES } from '@src/quince-components/src/atoms/button/button.constants'
import { EDDMessageContainer } from '@src/quince-components/src/atoms/eddMessageContainer'
import { ProductCTAConstants } from '@src/quince-components/src/atoms/productCTA/constants'
import { ProductDisplayButton } from '@src/quince-components/src/atoms/productCTA/product-display-button'
import { ProductDisplayText } from '@src/quince-components/src/atoms/productCTA/product-display-text'
import { InStockAndReadyToShip } from '@src/quince-components/src/atoms/productCTA/productCTA'
import { CTA_TYPE } from '@src/quince-components/src/atoms/productCTA/productCTAConstants'
import { getIsGiftCard } from '@src/quince-components/src/atoms/productCTA/utils'
import { VariantPurchaseInfo } from '@src/quince-components/src/atoms/productForm/ProductForm'
import { getProductLink } from '@src/quince-components/src/molecules/productCard/productCard.helpers'
import Product from '@src/quince-site-common/src/domains/Product'
import { ProductOption } from '@src/quince-site-common/src/domains/ProductOption'
import { ProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant'
import productHelper from '@src/quince-site-common/src/helpers/productHelper'
import { AppQRModal, useShopInAppHandler } from '@src/quince-site-common/src/hooks/appQrModal'
import useATCVsATBExperiment from '@src/quince-site-common/src/hooks/use-atc-vs-atb-experiment'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import AuthUtil from '@src/quince-site-common/src/utils/authUtil'
import { getProductPropertiesForTracking } from '@src/telemetry/getProductPropertiesForTracking'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

import styles from './miniPDPProductCTA.module.scss'
import { useMiniPDPProductCTA } from './useMiniPDPProductCTA'

const MiniPDPProductCTA = ({
  product,
  selectedOptions,
  type,
  onAddToCart,
  onJoinTheWaitList,
  showInStockAndReadyToShip,
  selectedVariantPurchaseInfo,
  errorMessage,
  setEmail,
  showWaitlistCTA,
}: {
  product: Product
  selectedOptions: ProductVariantOption[]
  type: CTA_TYPE
  selectedVariantPurchaseInfo: VariantPurchaseInfo | undefined
  onAddToCart: () => void
  onJoinTheWaitList: (email?: string) => void
  showInStockAndReadyToShip?: boolean
  errorMessage: string
  setEmail: Dispatch<SetStateAction<string>>
  showWaitlistCTA: boolean
}) => {
  const router = useRouter()
  const deviceInfo = useDeviceInfo()
  const { t } = useTranslation('common')
  const showATB = useATCVsATBExperiment()
  const atcLabel = showATB ? ProductCTAConstants.ADD_TO_BAG : ProductCTAConstants.ADD_TO_CART
  const isDesktop = deviceInfo.deviceType === DEVICE_TYPES.DESKTOP

  const productLink = getProductLink(product, selectedOptions)

  const { isAppQRModalOpen, handleShopInApp, handleCloseAppQRModal } = useShopInAppHandler()

  const handleFullDetailsClick = () => {
    const variants = productHelper.getVariantsForOptions(product, selectedOptions)
    const selectedVariant = variants[0]
    trackEvent(TELEMETRY_EVENT_NAMES.PRODUCT_FULL_DETAILS_VIEWED, {
      context: {
        productProperties: getProductPropertiesForTracking(product, selectedVariant?.svid?.toString(), {}),
      },
    })
    router.push(productLink)
  }

  const renderCTA = () => {
    const buttonSize = isDesktop ? BUTTON_SIZE.LARGE : BUTTON_SIZE.SMALL

    // Handle WAITLIST case with showWaitlistCTA=false as ADD_TO_CART
    const effectiveType = type === CTA_TYPE.WAITLIST && !showWaitlistCTA ? CTA_TYPE.ADD_TO_CART : type

    switch (effectiveType) {
      case CTA_TYPE.PREORDER:
        return (
          <ProductDisplayButton
            onClick={onAddToCart}
            buttonSize={buttonSize}
            showLoader={false}
            className={styles.productButton}
          >
            {t('preorder')}
          </ProductDisplayButton>
        )

      case CTA_TYPE.WAITLIST:
        return (
          <ProductDisplayButton
            onClick={() => {
              onJoinTheWaitList()
            }}
            buttonSize={buttonSize}
            showLoader={false}
            className={styles.productButton}
          >
            {selectedVariantPurchaseInfo?.hasJoinedWaitList
              ? t(ProductCTAConstants.WAITLISTED)
              : t(ProductCTAConstants.JOIN_WAITLIST)}
          </ProductDisplayButton>
        )

      case CTA_TYPE.SHOP_IN_THE_APP:
        return (
          <ProductDisplayButton
            onClick={() => {
              handleShopInApp({ product, selectedOptions })
            }}
            buttonSize={buttonSize}
            showLoader={false}
            className={styles.productButton}
            buttonType={BUTTON_TYPES.SECONDARY}
          >
            {t('shop_in_the_app')}
          </ProductDisplayButton>
        )

      default:
        return (
          <ProductDisplayButton
            onClick={onAddToCart}
            showLoader={false}
            className={styles.productButton}
            buttonSize={buttonSize}
          >
            {t(atcLabel)}
          </ProductDisplayButton>
        )
    }
  }

  return (
    <div className={styles.productCTAWrapper}>
      {showInStockAndReadyToShip ? (
        <div className={styles.stockMessageWrapper}>
          <InStockAndReadyToShip inStockMsgClass={styles.inStock} />
        </div>
      ) : null}
      {showWaitlistCTA && (
        <>
          <ProductDisplayText date={selectedVariantPurchaseInfo?.backInStockDate} className={styles.waitListedText} />
          {!selectedVariantPurchaseInfo?.hasJoinedWaitList && !AuthUtil.isLoggedIn() && (
            <input
              onChange={e => {
                setEmail?.(e.target.value)
              }}
              className={styles.waitlistInput}
              placeholder={t('your_email_address')}
              type="email"
            />
          )}
          {errorMessage && <div className={styles.errorMessage}>{errorMessage}</div>}
        </>
      )}
      <div>
        {isDesktop ? (
          renderCTA()
        ) : (
          <div className={styles.mobileCTAWrapper}>
            <ProductDisplayButton
              buttonType={BUTTON_TYPES.OUTLINED}
              onClick={handleFullDetailsClick}
              showLoader={false}
              className={styles.productButton}
              buttonSize={BUTTON_SIZE.SMALL}
            >
              {t('full_details')}
            </ProductDisplayButton>
            {renderCTA()}
          </div>
        )}
        {isDesktop && (
          <div className={styles.details}>
            <span className={styles.details__text}>
              <Link href={productLink} onClick={handleFullDetailsClick} aria-label={t('view_full_product_details')}>
                {t('view_full_details')}
              </Link>
            </span>
          </div>
        )}
      </div>
      <AppQRModal
        isOpen={isAppQRModalOpen}
        product={product}
        selectedOptions={selectedOptions}
        onClose={handleCloseAppQRModal}
      />
    </div>
  )
}

const MiniPDPProductCTAWrapper = ({
  product,
  selectedOptions,
  productOptions,
}: {
  product: Product
  selectedOptions: ProductVariantOption[]
  productOptions: ProductOption[]
}) => {
  const {
    selectedVariantPurchaseInfo,
    onAddToCart,
    onJoinTheWaitList,
    showInStockAndReadyToShip,
    errorMessage,
    setEmail,
    showWaitlistCTA,
    productCTAType,
  } = useMiniPDPProductCTA({
    product,
    selectedOptions,
    productOptions,
  })

  const deviceInfo = useDeviceInfo()
  const isDesktop = deviceInfo.deviceType === DEVICE_TYPES.DESKTOP

  const { experimentBucket } = useStatsigExperiment(STATSIG_EXPERIMENTS.EDD)
  const isEDDExpEnabled = experimentBucket !== STATSIG_EXPERIMENTS_CONFIG[STATSIG_EXPERIMENTS.EDD].buckets?.control

  const eddDetail = selectedVariantPurchaseInfo?.eddDetail

  const showEDDMsg =
    !(
      productCTAType === CTA_TYPE.WAITLIST ||
      selectedVariantPurchaseInfo?.isPreorder ||
      selectedVariantPurchaseInfo?.showMadeToOrder ||
      getIsGiftCard(product?.id ?? '')
    ) &&
    isEDDExpEnabled &&
    eddDetail?.showEDD !== undefined

  return (
    <>
      {showEDDMsg ? (
        <div className={styles.stockMessageWrapper}>
          <EDDMessageContainer
            eddDetail={eddDetail}
            product={product}
            selectedInternalVariantId={selectedVariantPurchaseInfo?.selectedInternalVariantId}
            containerClassName={styles.eddContainer}
            showIcon={!isDesktop}
          />
        </div>
      ) : null}
      <MiniPDPProductCTA
        product={product}
        selectedOptions={selectedOptions}
        type={productCTAType}
        selectedVariantPurchaseInfo={selectedVariantPurchaseInfo}
        onAddToCart={onAddToCart}
        onJoinTheWaitList={onJoinTheWaitList}
        showInStockAndReadyToShip={showInStockAndReadyToShip}
        errorMessage={errorMessage}
        setEmail={setEmail}
        showWaitlistCTA={showWaitlistCTA}
      />
    </>
  )
}
export default MiniPDPProductCTAWrapper
